import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CompetitorOrganisation {
  id: string;
  name: string;
  accent_color: string;
  logo_url: string | null;
  website: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

interface CompetitorSitesBulkImportProps {
  organisations: CompetitorOrganisation[];
  onImportComplete: () => void;
}

interface ParsedSite {
  organisation_name: string;
  name: string;
  address?: string;
  postal_code?: string;
  city: string;
  latitude?: number;
  longitude?: number;
  chargers_count?: number;
  power_kw?: number;
  notes?: string;
}

interface ImportStats {
  total: number;
  processed: number;
  success: number;
  errors: string[];
}

const COLUMN_MAPPING: Record<string, keyof ParsedSite> = {
  'Organisatie': 'organisation_name',
  'Site Naam': 'name',
  'Adres': 'address',
  'Postcode': 'postal_code',
  'Stad': 'city',
  'Latitude': 'latitude',
  'Longitude': 'longitude',
  'Aantal Laadpunten': 'chargers_count',
  'Vermogen (kW)': 'power_kw',
  'Notities': 'notes'
};

async function geocodeAddress(
  address?: string,
  postalCode?: string,
  city?: string
): Promise<{ lat: number; lng: number } | null> {
  if (!city) return null;

  const parts = [address, postalCode, city, 'Netherlands'].filter(Boolean);
  const query = parts.join(', ');

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(query)}` +
      `&format=json&limit=1&countrycodes=nl`,
      {
        headers: {
          'User-Agent': 'GreenVision-CRM/1.0'
        }
      }
    );

    const data = await response.json();
    if (data[0]) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }

  return null;
}

export function CompetitorSitesBulkImport({ 
  organisations, 
  onImportComplete 
}: CompetitorSitesBulkImportProps) {
  const [parsedData, setParsedData] = useState<ParsedSite[]>([]);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleDownloadTemplate = () => {
    const headers = Object.keys(COLUMN_MAPPING);

    const exampleData = [
      [
        'LEAP24',
        'LEAP24 Amsterdam Westpoort',
        'Contactweg 36',
        '1014 AN',
        'Amsterdam',
        '52.3924',
        '4.8726',
        '8',
        '400',
        'Operationeel'
      ],
      [
        'Milence',
        'Milence Venlo A67',
        'A67',
        '',
        'Venlo',
        '',
        '',
        '12',
        '600',
        'In aanbouw'
      ]
    ];

    const wsData = [headers, ...exampleData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    ws['!cols'] = [
      { wch: 15 }, // Organisatie
      { wch: 30 }, // Site Naam
      { wch: 25 }, // Adres
      { wch: 10 }, // Postcode
      { wch: 15 }, // Stad
      { wch: 12 }, // Latitude
      { wch: 12 }, // Longitude
      { wch: 18 }, // Aantal Laadpunten
      { wch: 15 }, // Vermogen
      { wch: 30 }  // Notities
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Competitor Sites');
    XLSX.writeFile(wb, 'competitor_sites_template.xlsx');

    toast.success('Template gedownload');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsed: ParsedSite[] = jsonData.map((row: any) => {
        const site: any = {};
        Object.entries(COLUMN_MAPPING).forEach(([excelCol, fieldName]) => {
          const value = row[excelCol];
          if (value !== undefined && value !== null && value !== '') {
            if (fieldName === 'latitude' || fieldName === 'longitude' || 
                fieldName === 'chargers_count' || fieldName === 'power_kw') {
              site[fieldName] = parseFloat(value);
            } else {
              site[fieldName] = value;
            }
          }
        });
        return site;
      });

      // Validatie
      const errors: string[] = [];
      parsed.forEach((site, idx) => {
        if (!site.organisation_name || !site.name || !site.city) {
          errors.push(`Rij ${idx + 2}: Organisatie, Site Naam en Stad zijn verplicht`);
        }
        if ((site.latitude && !site.longitude) || (!site.latitude && site.longitude)) {
          errors.push(`Rij ${idx + 2}: Latitude en Longitude moeten beide ingevuld zijn of beide leeg`);
        }
      });

      if (errors.length > 0) {
        toast.error('Validatie fouten gevonden');
        errors.forEach(err => toast.error(err));
        return;
      }

      setParsedData(parsed);
      toast.success(`${parsed.length} sites ingelezen en gevalideerd`);
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Fout bij inlezen Excel bestand');
    }

    e.target.value = '';
  };

  const handleImport = async () => {
    setIsImporting(true);
    setImportStats({ total: parsedData.length, processed: 0, success: 0, errors: [] });

    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];

      try {
        // Find organisation
        const org = organisations.find(o =>
          o.name.toLowerCase() === row.organisation_name.toLowerCase()
        );

        if (!org) {
          throw new Error(`Organisatie "${row.organisation_name}" niet gevonden in systeem`);
        }

        // Geocode if needed
        let lat = row.latitude;
        let lng = row.longitude;

        if (!lat || !lng) {
          const coords = await geocodeAddress(
            row.address,
            row.postal_code,
            row.city
          );

          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
          } else {
            throw new Error('Kon locatie niet geocoden');
          }

          // Rate limiting voor Nominatim
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        // Check duplicates
        const { data: existing } = await supabase
          .from('competitor_sites')
          .select('id')
          .eq('organisation_id', org.id)
          .eq('name', row.name)
          .maybeSingle();

        if (existing) {
          // Update existing
          const { error } = await supabase
            .from('competitor_sites')
            .update({
              address: row.address || null,
              city: row.city,
              latitude: lat,
              longitude: lng,
              chargers_count: row.chargers_count || null,
              power_kw: row.power_kw || null,
              notes: row.notes || null,
              updated_at: new Date().toISOString()
            })
            .eq('id', existing.id);

          if (error) throw error;
        } else {
          // Insert new
          const { error } = await supabase
            .from('competitor_sites')
            .insert({
              organisation_id: org.id,
              name: row.name,
              address: row.address || null,
              city: row.city,
              latitude: lat,
              longitude: lng,
              chargers_count: row.chargers_count || null,
              power_kw: row.power_kw || null,
              notes: row.notes || null
            });

          if (error) throw error;
        }

        setImportStats(prev => prev ? {
          ...prev,
          processed: prev.processed + 1,
          success: prev.success + 1
        } : null);

      } catch (error: any) {
        console.error(`Import error for row ${i}:`, error);
        setImportStats(prev => prev ? {
          ...prev,
          processed: prev.processed + 1,
          errors: [...prev.errors, `Rij ${i + 2} (${row.name}): ${error.message}`]
        } : null);
      }
    }

    setIsImporting(false);
    toast.success(`Import voltooid: ${importStats?.success || 0} sites verwerkt`);
    if (importStats && importStats.errors.length > 0) {
      toast.error(`${importStats.errors.length} fouten opgetreden`);
    }
    
    onImportComplete();
    setParsedData([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          Download Template
        </Button>

        <Button
          variant="outline"
          className="gap-2"
          onClick={() => document.getElementById('bulk-upload')?.click()}
        >
          <Upload className="w-4 h-4" />
          Upload Excel
        </Button>
        <input
          id="bulk-upload"
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>

      {parsedData.length > 0 && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Preview: {parsedData.length} sites</h3>
              <Button
                onClick={handleImport}
                disabled={isImporting}
                className="gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {isImporting ? 'Importeren...' : 'Bevestig Import'}
              </Button>
            </div>

            {isImporting && importStats && (
              <div className="space-y-2">
                <Progress 
                  value={(importStats.processed / importStats.total) * 100} 
                />
                <p className="text-sm text-muted-foreground">
                  {importStats.processed} van {importStats.total} verwerkt ({importStats.success} succesvol)
                </p>
              </div>
            )}

            {importStats && importStats.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importStats.errors.map((err, idx) => (
                      <div key={idx} className="text-xs">{err}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="max-h-96 overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisatie</TableHead>
                    <TableHead>Site Naam</TableHead>
                    <TableHead>Stad</TableHead>
                    <TableHead>Adres</TableHead>
                    <TableHead>Coördinaten</TableHead>
                    <TableHead>Laadpunten</TableHead>
                    <TableHead>Vermogen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedData.map((site, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{site.organisation_name}</TableCell>
                      <TableCell>{site.name}</TableCell>
                      <TableCell>{site.city}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {site.address || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {site.latitude && site.longitude 
                          ? `${site.latitude.toFixed(4)}, ${site.longitude.toFixed(4)}`
                          : 'Auto-geocode'
                        }
                      </TableCell>
                      <TableCell>{site.chargers_count || '-'}</TableCell>
                      <TableCell>{site.power_kw ? `${site.power_kw} kW` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-1 text-sm">
            <p><strong>Verplichte kolommen:</strong> Organisatie, Site Naam, Stad</p>
            <p><strong>Geocoding:</strong> Vul Latitude + Longitude in voor exacte locaties, of laat leeg voor automatische geocoding via adres</p>
            <p><strong>Duplicaten:</strong> Bestaande sites (zelfde naam + organisatie) worden automatisch bijgewerkt</p>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
